import { useMemo } from 'react';
import { useBodyScrollLock } from '../hooks';
import { parseUTCDate, useLanguage } from '../i18n';

interface FarewellModalProps {
  isOpen: boolean;
  onClose: () => void;
  payload?: {
    created_at: string | null;
    name: string | null;
    email: string | null;
  } | null;
}

function getDaysTogether(createdAt: string): number {
  const created = parseUTCDate(createdAt);
  const now = new Date();
  const diffTime = now.getTime() - created.getTime();
  return Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1);
}

export default function FarewellModal({ isOpen, onClose, payload }: FarewellModalProps) {
  const { t, lang } = useLanguage();
  useBodyScrollLock(isOpen);

  const daysTogether = useMemo(() => {
    if (!payload?.created_at) return null;
    try {
      return getDaysTogether(payload.created_at);
    } catch {
      return null;
    }
  }, [payload?.created_at]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="farewell-modal-overlay"
    >
      <div
        className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid="farewell-modal"
      >
        <div className="p-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 bg-status-warning/15 rounded-full flex items-center justify-center">
            <span className="text-2xl">😢</span>
          </div>
          <h3 className="text-lg font-semibold text-terminal-text mb-2">
            {t.profile.farewellTitle}
          </h3>
          <p className="text-sm text-terminal-muted leading-relaxed whitespace-pre-line">
            {t.profile.farewellMessage}
          </p>
          {daysTogether !== null && (
            <p className="text-sm text-terminal-text/90 mt-3 font-semibold">
              {t.profile.farewellTogetherLine.replace('{days}', String(daysTogether))}
            </p>
          )}
          <p className="text-xs text-terminal-dim mt-2 whitespace-pre-line">
            {t.profile.farewellSeeYouLine}
          </p>
          <p className="text-xs text-terminal-dim mt-1">
            {t.profile.farewellSubMessage}
          </p>

          <button
            onClick={onClose}
            className="w-full mt-6 py-3 bg-status-go hover:bg-status-go/90 border border-status-go rounded-lg text-white text-sm font-medium transition-colors"
          >
            {t.profile.farewellCta || (lang === 'ko' ? '확인' : 'OK')}
          </button>
        </div>
      </div>
    </div>
  );
}


